import { useId, useRef, useState } from "react";
import { ImagePlus, Replace, Trash2 } from "lucide-react";
import { cn } from "../../lib/utils.js";
import { Button } from "../ui/button/index.js";

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export function ImageDropzone({
  previewUrl,
  fileName,
  byteSize,
  detail,
  accept,
  disabled = false,
  busy = false,
  error = null,
  selectLabel,
  changeLabel,
  removeLabel,
  dropLabel,
  emptyHint,
  onSelectFile,
  onRemove,
}: {
  previewUrl: string | null;
  fileName?: string | null;
  byteSize?: number | null;
  detail?: string | null;
  accept: string;
  disabled?: boolean;
  busy?: boolean;
  error?: string | null;
  selectLabel: string;
  changeLabel: string;
  removeLabel: string;
  dropLabel: string;
  emptyHint?: string;
  onSelectFile: (file: File) => void;
  onRemove?: () => void;
}): React.JSX.Element {
  const inputId = useId();
  const inputRef = useRef<HTMLInputElement>(null);
  const [dragOver, setDragOver] = useState(false);
  const hasImage = Boolean(previewUrl);

  function pickFromList(files: FileList | null): void {
    const file = files?.[0];
    if (file) onSelectFile(file);
  }

  return (
    <div className="grid gap-2">
      <div
        className={cn(
          "relative grid min-h-[10.5rem] place-items-center overflow-hidden rounded-lg border border-dashed px-3 py-4 transition-[border-color,background-color] duration-150",
          dragOver
            ? "border-accent bg-accent/8"
            : "border-line bg-canvas",
          error ? "border-destructive" : null,
          disabled ? "opacity-60" : null,
        )}
        onDragEnter={(event) => {
          event.preventDefault();
          if (!disabled && !busy) setDragOver(true);
        }}
        onDragOver={(event) => {
          event.preventDefault();
        }}
        onDragLeave={() => setDragOver(false)}
        onDrop={(event) => {
          event.preventDefault();
          setDragOver(false);
          if (disabled || busy) return;
          pickFromList(event.dataTransfer.files);
        }}
      >
        <input
          ref={inputRef}
          id={inputId}
          type="file"
          accept={accept}
          className="sr-only"
          disabled={disabled || busy}
          onChange={(event) => {
            pickFromList(event.target.files);
            event.currentTarget.value = "";
          }}
        />
        {hasImage ? (
          <div className="grid w-full max-w-full justify-items-center gap-3">
            <img
              src={previewUrl!}
              alt=""
              className="max-h-40 w-full max-w-full object-contain"
            />
            <div className="grid justify-items-center gap-0.5 text-center">
              {fileName ? (
                <p className="m-0 max-w-full truncate text-[0.8rem] font-medium text-ink">
                  {fileName}
                </p>
              ) : null}
              {byteSize != null ? (
                <p className="m-0 text-[0.75rem] text-ink-muted">{formatBytes(byteSize)}</p>
              ) : null}
              {detail ? (
                <p className="m-0 text-[0.75rem] text-ink-muted" dir="ltr">
                  {detail}
                </p>
              ) : null}
            </div>
          </div>
        ) : (
          <label
            htmlFor={inputId}
            className={cn(
              "grid cursor-pointer justify-items-center gap-2 text-center text-ink-muted",
              disabled || busy ? "cursor-not-allowed" : null,
            )}
          >
            <span className="grid size-11 place-items-center rounded-full bg-surface text-accent">
              <ImagePlus size={20} aria-hidden />
            </span>
            <span className="text-sm font-medium text-ink">{selectLabel}</span>
            <span className="text-[0.75rem]">{dropLabel}</span>
            {emptyHint ? <span className="text-[0.75rem]">{emptyHint}</span> : null}
          </label>
        )}
        {busy ? (
          <div className="absolute inset-0 grid place-items-center bg-surface/70 text-sm font-medium text-ink">
            …
          </div>
        ) : null}
      </div>
      {hasImage ? (
        <div className="flex flex-wrap gap-2">
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={disabled || busy}
            onClick={() => inputRef.current?.click()}
          >
            <Replace size={14} aria-hidden />
            {changeLabel}
          </Button>
          {onRemove ? (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              disabled={disabled || busy}
              onClick={onRemove}
            >
              <Trash2 size={14} aria-hidden />
              {removeLabel}
            </Button>
          ) : null}
        </div>
      ) : null}
      {error ? (
        <p className="m-0 text-[0.8rem] text-destructive" role="alert">
          {error}
        </p>
      ) : null}
    </div>
  );
}
